import React, { useRef, useEffect, useState } from 'react';
import '../styles/Asteroids.css'; // Assuming DarkMode.css handles the theme classes

// Add particle type definition
type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  size: number;
};

const Asteroids: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  let animationFrameId: number;

  const ship = { x: 0, y: 0, angle: 0 };
  const keys: { [key: string]: boolean } = {};
  const particles: Particle[] = [];

  const bullets: { x: number; y: number; vx: number; vy: number }[] = [];
  const asteroids: { x: number; y: number; size: number; vx: number; vy: number; shape: { x: number; y: number }[] }[] = [];

  const asteroidImage = new Image();
  asteroidImage.src = '/images/asteroid.png';

  const shipImage = new Image();
  shipImage.src = '/images/ship.png';

  let lastShotTime = 0;
  const shotCooldown = 100; // Cooldown period in milliseconds

  // Load audio files only if not on mobile
  const backgroundMusic = !isMobile ? new Audio('/sounds/music.mp3') : null;
  const shootSound = !isMobile ? new Audio('/sounds/lazer.mp3') : null;
  const explosionSound = !isMobile ? new Audio('/sounds/bang.mp3') : null;
  const gameOverSound = !isMobile ? new Audio('/sounds/gameover3.mp3') : null;

  const playBackgroundMusic = () => {
    if (backgroundMusic && !isMobile) { // Only play sound if not on mobile
      backgroundMusic.loop = true;
      backgroundMusic.volume = 0.2; // Adjust volume based on isMuted
      backgroundMusic.play();
    }
  };

  // Stop background music
  const stopBackgroundMusic = () => {
    if (backgroundMusic) {
      backgroundMusic.pause();
      backgroundMusic.currentTime = 0;
    }
  };

  // Play sound effect
  const playSound = (sound: HTMLAudioElement | null) => {
    if (sound && !isMobile && !gameOver) {
      if (sound === shootSound) {
        sound.volume = 0.3; // Lower the volume for the lazer sound
      } else {
        sound.volume = 1;
      }
      sound.currentTime = 0;
      sound.play();
    }
  };

  const createAsteroid = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;

    // Determine which edge to spawn from (0: top, 1: right, 2: bottom, 3: left)
    const spawnEdge = Math.floor(Math.random() * 4);
    let x, y;

    // Calculate spawn position based on edge
    switch (spawnEdge) {
      case 0: // Top
        x = Math.random() * canvas.width;
        y = -30; // Spawn above the canvas
        break;
      case 1: // Right
        x = canvas.width + 30;
        y = Math.random() * canvas.height;
        break;
      case 2: // Bottom
        x = Math.random() * canvas.width;
        y = canvas.height + 30;
        break;
      case 3: // Left
        x = -30;
        y = Math.random() * canvas.height;
        break;
      default:
        return;
    }

    const size = Math.random() * 30 + 20;

    // Calculate random angle with some variation
    const baseAngle = Math.random() * Math.PI * 2; // Random base angle
    const angleVariation = (Math.random() - 0.5) * Math.PI; // Random variation of ±90 degrees
    const angle = baseAngle + angleVariation;

    // Adjust speed based on device type with more randomness
    const baseSpeed = window.innerWidth <= 1300 ? 4 : 5.5; // Increased from 2.5/1.5 to 3.5/2.5
    const speed = Math.random() * baseSpeed + 0.5; // Random speed
    const vx = Math.cos(angle) * speed;
    const vy = Math.sin(angle) * speed;

    // Precompute the shape
    const sides = Math.floor(Math.random() * 5) + 5; // Random number of sides
    const shape: { x: number; y: number }[] = [];
    const angleStep = (Math.PI * 2) / sides;
    for (let i = 0; i < sides; i++) {
      const radiusVariation = Math.random() * 0.4 + 0.8; // Random variation in radius
      const x = Math.cos(angleStep * i) * size * radiusVariation;
      const y = Math.sin(angleStep * i) * size * radiusVariation;
      shape.push({ x, y });
    }

    asteroids.push({ x, y, size, vx, vy, shape });
  };

  const createExplosion = (x: number, y: number) => {
    const particleCount = 8;
    for (let i = 0; i < particleCount; i++) {
      const angle = (Math.PI * 2 * i) / particleCount;
      const speed = 1 + Math.random() * 2;
      particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1,
        size: 2 + Math.random() * 2
      });
    }
  };

  const drawParticles = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;

    for (let i = particles.length - 1; i >= 0; i--) {
      const particle = particles[i];
      particle.x += particle.vx;
      particle.y += particle.vy;
      particle.life -= 0.02;

      context.beginPath();
      context.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      context.fillStyle = `rgba(255, 255, 0, ${particle.life})`;
      context.fill();

      if (particle.life <= 0) {
        particles.splice(i, 1);
      }
    }
  };

  const update = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;

    // Clear canvas
    context.fillStyle = 'black';
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Draw spaceship
    drawSpaceship();

    // Move spaceship
    if (keys['ArrowUp']) {
      ship.x += Math.cos(ship.angle) * 3.5;
      ship.y += Math.sin(ship.angle) * 3.5;
    }
    if (keys['ArrowLeft']) ship.angle -= 0.05;
    if (keys['ArrowRight']) ship.angle += 0.05;

    // Screen wrapping for spaceship
    if (ship.x < 0) ship.x = canvas.width;
    if (ship.x > canvas.width) ship.x = 0;
    if (ship.y < 0) ship.y = canvas.height;
    if (ship.y > canvas.height) ship.y = 0;

    // Draw and move bullets
    bullets.forEach((bullet, index) => {
      bullet.x += bullet.vx;
      bullet.y += bullet.vy;
      drawBullet(bullet);

      // Remove bullets that are out of bounds
      if (bullet.x < 0 || bullet.x > canvas.width || bullet.y < 0 || bullet.y > canvas.height) {
        bullets.splice(index, 1);
      }
    });

    // Draw and move asteroids
    asteroids.forEach((asteroid, index) => {
      drawAsteroid(asteroid);

      // Move asteroid
      asteroid.x += asteroid.vx;
      asteroid.y += asteroid.vy;

      // Wrap around screen
      if (asteroid.x < 0) asteroid.x = canvas.width;
      if (asteroid.x > canvas.width) asteroid.x = 0;
      if (asteroid.y < 0) asteroid.y = canvas.height;
      if (asteroid.y > canvas.height) asteroid.y = 0;

      // Collision detection with bullets
      bullets.forEach((bullet, bIndex) => {
        const dx = bullet.x - asteroid.x;
        const dy = bullet.y - asteroid.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < asteroid.size) {
          createExplosion(asteroid.x, asteroid.y); // Add explosion effect
          playSound(explosionSound); // Play explosion sound
          asteroids.splice(index, 1);
          bullets.splice(bIndex, 1);
          setScore((prevScore) => prevScore + 100);
          createAsteroid(); // Regenerate asteroid
        }
      });

      // Collision detection with spaceship
      const dx = ship.x - asteroid.x;
      const dy = ship.y - asteroid.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const collisionDistance = asteroid.size + 5; // Adjusted for spaceship size
      if (distance < collisionDistance) {
        setGameOver(true);
        stopBackgroundMusic(); // Stop background music on game over
        cancelAnimationFrame(animationFrameId);
        return; // Exit the loop to prevent further updates
      }
    });

    // Draw particles
    drawParticles();

    if (!gameOver) {
      animationFrameId = requestAnimationFrame(update);
    }
  };

  const drawSpaceship = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;

    context.save();
    context.translate(ship.x, ship.y);
    context.rotate(ship.angle);
    // Draw the spaceship image
    context.drawImage(shipImage, -15, -15, 30, 30); // Adjust size as needed
    context.restore();
  };

  const drawBullet = (bullet: { x: number; y: number }) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;

    context.beginPath();
    context.arc(bullet.x, bullet.y, 2, 0, Math.PI * 2);
    context.fillStyle = 'yellow';
    context.fill();
  };

  const drawAsteroid = (asteroid: { x: number; y: number; size: number; shape: { x: number; y: number }[] }) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;

    // Draw the asteroid image
    context.drawImage(asteroidImage, asteroid.x - asteroid.size / 2, asteroid.y - asteroid.size / 2, asteroid.size, asteroid.size);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;

    // Update canvas dimensions to match CSS
    const updateCanvasDimensions = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
    };

    // Call the function to update dimensions
    updateCanvasDimensions();

    // Add event listener for window resize
    window.addEventListener('resize', updateCanvasDimensions);

    ship.x = canvas.width / 2;
    ship.y = canvas.height / 2;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent default behavior for arrow keys and spacebar to avoid page scrolling
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' '].includes(e.key)) {
        e.preventDefault();
      }

      keys[e.key] = true;
      if (e.key === ' ') {
        const currentTime = Date.now();
        if (currentTime - lastShotTime >= shotCooldown) {
          // Calculate the nose position of the spaceship
          const noseX = ship.x + Math.cos(ship.angle - Math.PI / 2) * 15;
          const noseY = ship.y + Math.sin(ship.angle - Math.PI / 2) * 15;

          // Shoot bullet from the front of the spaceship
          const bullet = {
            x: noseX,
            y: noseY,
            vx: Math.cos(ship.angle - Math.PI / 2) * 7,
            vy: Math.sin(ship.angle - Math.PI / 2) * 7,
          };
          bullets.push(bullet);
          lastShotTime = currentTime; // Update last shot time
          playSound(shootSound); // Play shooting sound
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      keys[e.key] = false;
    };

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      const touchX = touch.clientX;
      const touchY = touch.clientY;

      // Determine if the touch is on the left or right side of the screen
      if (touchX < window.innerWidth / 2) {
        keys['ArrowLeft'] = true;
      } else {
        keys['ArrowRight'] = true;
      }

      // Determine if the touch is on the top or bottom half of the screen
      if (touchY < window.innerHeight / 2) {
        keys['ArrowUp'] = true;
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      keys['ArrowLeft'] = false;
      keys['ArrowRight'] = false;
      keys['ArrowUp'] = false;
    };

    const handleTouchTap = (e: TouchEvent) => {
      // Fire bullet on tap
      const noseX = ship.x + Math.cos(ship.angle - Math.PI / 2) * 15;
      const noseY = ship.y + Math.sin(ship.angle - Math.PI / 2) * 15;

      const bullet = {
        x: noseX,
        y: noseY,
        vx: Math.cos(ship.angle - Math.PI / 2) * 7,
        vy: Math.sin(ship.angle - Math.PI / 2) * 7,
      };
      bullets.push(bullet);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchend', handleTouchTap);

    // Create initial asteroids
    for (let i = 0; i < 5; i++) createAsteroid();

    if (gameStarted && !gameOver) {
      playBackgroundMusic(); // Start background music
      update();
    } else {
      stopBackgroundMusic(); // Stop background music when game is not started or game is over
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchend', handleTouchTap);
      window.removeEventListener('resize', updateCanvasDimensions); // Remove the resize event listener
      stopBackgroundMusic(); // Stop background music
      cancelAnimationFrame(animationFrameId);
    };
  }, [gameOver, gameStarted]);

  useEffect(() => {
    if (gameOver && gameOverSound) {
      gameOverSound.volume = 0.1 // Lower the volume
      gameOverSound.currentTime = 0; // Reset sound to start
      gameOverSound.play(); // Play game over sound
    }
  }, [gameOver]);

  const startGame = () => {
    // Clear existing asteroids, bullets, and particles
    asteroids.length = 0;
    bullets.length = 0;
    particles.length = 0;

    // Reset ship position to center
    const canvas = canvasRef.current;
    if (!canvas) return;
    ship.x = canvas.width / 2;
    ship.y = canvas.height / 2;
    ship.angle = 0;

    // Reset game state
    setScore(0);
    setGameOver(false);
    setGameStarted(true);

    // Create initial asteroids with a slight delay to prevent immediate collision
    setTimeout(() => {
      for (let i = 0; i < 5; i++) createAsteroid();
    }, 100);
  };

  useEffect(() => {
    // Detect if the user is on a mobile device
    const isMobileDevice = /Mobi|Android/i.test(navigator.userAgent);
    setIsMobile(isMobileDevice);
  }, []);

  return (
    <main>
      <div><h1 className="pageTitle">Asteroids</h1> </div>
      <div className='game-container'>
        <div className='score'>Score: {score}</div>
        {!gameStarted && <div className='game-over'><button onClick={startGame}>Start</button></div>}
        {gameOver && <div className='game-over'>Game Over! <button onClick={startGame}>Play Again</button></div>}
        <canvas ref={canvasRef} width={1100} height={466} />
        <div className='instructions'>
          <ul>
          <li><b>PC:</b> Spacebar to shoot, Arrow keys to move/rotate ship </li> 
          <li><b>Mobile:</b> Tap to shoot, press top left and right of canvas to move ship, press bottom left and right of canvas to rotate ship</li>
          </ul>
          </div>

      </div>
    </main>
  );
};

export default Asteroids;
